import { axiosInstance } from 'common/infrastructure/api/http-client';
import axios, { AxiosRequestConfig } from 'axios';
import { VITE_ENV } from 'config';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { clearCache } from 'common/infrastructure/cacheBuster/cacheBusterUtils';
import { savePausedMutations } from 'common/infrastructure/remoteState/persistQueryClient';
import { PATH_AUTH } from 'routes/paths';

// Track logout state to avoid repeated/logout loops
let logoutInProgress = false;

export const isLogoutInProgress = (): boolean => logoutInProgress;

export const setLogoutInProgress = (inProgress: boolean): void => {
  logoutInProgress = inProgress;
};

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export const getBearerTokenFromHeader = (authHeader: string) => {
  if (!(authHeader && authHeader.startsWith('Bearer '))) {
    return '';
  }

  return authHeader.substring(7, authHeader.length);
};

export const isTokenExpirationValid = (token: string) => {
  if (!token) {
    return false;
  }

  const decoded = jwtDecode(token);

  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

// eslint-disable-next-line import/no-unused-modules
export const setTokenTimeout = (exp: number) => {
  // Don't set refresh timer if logout is in progress
  if (isLogoutInProgress()) {
    return;
  }

  // eslint-disable-next-line prefer-const
  let expiredTimer;

  const currentTime = Date.now();

  // Test token expires after 10s
  // const timeLeft = currentTime + 10000 - currentTime; // ~10s
  const timeLeft = exp * 1000 - currentTime;

  clearTimeout(expiredTimer);

  expiredTimer = setTimeout(async () => {
    if (axiosInstance.defaults.headers.common.Authorization) {
      await refreshToken();
    }
  }, timeLeft);
};

// ----------------------------------------------------------------------

export const setSession = (idToken: string | null) => {
  if (idToken) {
    localStorage.setItem('idToken', idToken);

    axiosInstance.defaults.headers.common.Authorization = getAuthorizationHeader(idToken);
  } else {
    clearSession();
  }
};

export const getAuthorizationHeader = (idToken: string) => `Bearer ${idToken}`;

// ----------------------------------------------------------------------

// eslint-disable-next-line import/no-unused-modules
export const clearSession = () => {
  localStorage.removeItem('idToken');
  delete axiosInstance.defaults.headers.common.Authorization;
};

export const externalLogout = async ({
  skipRefreshTokenRevocation = false,
  skipCacheBusting = false,
  skipLogoutWhenOffline = false,
}: {
  skipRefreshTokenRevocation?: boolean;
  skipCacheBusting?: boolean;
  skipLogoutWhenOffline?: boolean;
} = {}) => {
  // Prevent re-entrant logout loops
  if (logoutInProgress) {
    loggerService.log(
      LoggerLevel.DEBUG,
      '[LOGOUT] Logout already in progress, skipping duplicate call'
    );
    return;
  }

  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';
  const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;
  const isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
  const stackTrace = new Error().stack;

  loggerService.log(LoggerLevel.DEBUG, '[LOGOUT] externalLogout called', {
    skipRefreshTokenRevocation,
    skipCacheBusting,
    skipLogoutWhenOffline,
    hasToken: !!idToken,
    tokenValid,
    isOffline,
    appVersion: typeof window !== 'undefined' ? window.appVersion : 'unknown',
    stackTrace: stackTrace?.split('\n').slice(0, 10).join('\n'), // First 10 lines of stack
  });

  if (skipLogoutWhenOffline && isOffline) {
    loggerService.log(LoggerLevel.DEBUG, '[LOGOUT] Skipping logout because user is offline');
    return;
  }

  setLogoutInProgress(true);

  let redirectUrl = getLogoutUrl();

  if (VITE_ENV.isLocalhost) {
    redirectUrl = PATH_AUTH.login;
  }

  // Perform token revocation first (while we still have valid auth), then cache busting
  const operations = [];

  if (!skipRefreshTokenRevocation) {
    operations.push(
      (async () => {
        try {
          await revokeRefreshToken();
        } catch (error) {
          loggerService.log(LoggerLevel.ERROR, 'Failed to revokeRefreshToken', error);
        }
      })()
    );
  }

  if (!skipCacheBusting) {
    operations.push(
      (async () => {
        try {
          loggerService.log(LoggerLevel.DEBUG, 'Performing cache bust on logout...');
          await clearCache();
        } catch (error) {
          loggerService.log(
            LoggerLevel.ERROR,
            'Cache busting on logout failed, proceeding with normal logout',
            error
          );
        }
      })()
    );
  }

  // Wait for operations to complete
  await Promise.allSettled(operations);

  try {
    await savePausedMutations();
  } catch (error) {
    loggerService.log(
      LoggerLevel.WARN,
      '[LOGOUT] Failed to preserve paused mutations before session clear',
      error
    );
  }

  // Clear session after token revocation
  clearSession();

  window.location.href = redirectUrl;
};

// ----------------------------------------------------------------------

// eslint-disable-next-line import/no-unused-modules
export const getRefreshTokenEndpoint = (): string => `${VITE_ENV.authUrl}/auth/token`;

export const isAuthRequest = (url: string): boolean => url.startsWith(VITE_ENV.authUrl);

export const isRefreshRequest = (url: string, method: string): boolean => {
  if (method !== 'put') {
    return false;
  }

  return url === getRefreshTokenEndpoint();
};

const TOKEN_REFRESH_MAX_RETRIES = 4;
const TOKEN_REFRESH_INITIAL_DELAY_MS = 1000;

/**
 * Determines if a token refresh error is a transient network issue
 * that should be retried vs a definitive auth failure that should trigger logout.
 *
 * Transient errors include: CORS failures, network timeouts, connection resets,
 * and any error where no HTTP response was received from the server.
 *
 * Definitive auth failures include: 401 (expired refresh token), 403 (forbidden).
 *
 * Note: errors reach here after passing through the HTTP client error interceptor,
 * which transforms them into either strings ("HTTP -1: Something went wrong") or
 * HttpErrorResponse objects ({ status, data, message }). The checks below handle
 * both the raw Axios error form and the interceptor-transformed forms.
 */
const isTransientRefreshError = (error: unknown): boolean => {
  // Axios errors with no response = network/CORS failure (request never reached server)
  if (axios.isAxiosError(error) && !error.response) {
    return true;
  }

  // Errors that went through the error interceptor and were rejected as strings
  // with status -1 (no response received) — this is the most common case for
  // CORS/network failures after the interceptor transforms them
  if (typeof error === 'string' && error.includes('HTTP -1')) {
    return true;
  }

  // HttpErrorResponse objects from the interceptor with no response (status -1)
  // or server errors (5xx) which are transient
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const { status } = error as { status: number };
    if (status === -1 || status >= 500) {
      return true;
    }
  }

  // Raw Axios server errors (5xx) — in case the error bypasses the interceptor
  if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
    return true;
  }

  return false;
};

export const refreshToken = async (idToken?: string): Promise<string> => {
  // Don't refresh token if logout is in progress
  if (isLogoutInProgress()) {
    loggerService.log(LoggerLevel.DEBUG, '[TOKEN_REFRESH] Skipping refresh - logout in progress');
    return '';
  }

  const config: AxiosRequestConfig = {};
  // If there is no id token, we can assume the access token is
  // already set as the default Authorization header.
  if (idToken) {
    config.headers = {
      Authorization: getAuthorizationHeader(idToken),
    };
  }

  const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;

  for (let attempt = 1; attempt <= TOKEN_REFRESH_MAX_RETRIES; attempt++) {
    // Don't retry if logout started during a previous attempt's delay
    if (isLogoutInProgress()) {
      loggerService.log(LoggerLevel.DEBUG, '[TOKEN_REFRESH] Skipping refresh - logout in progress');
      return '';
    }

    loggerService.log(LoggerLevel.DEBUG, '[TOKEN_REFRESH] Attempting token refresh', {
      attempt,
      maxRetries: TOKEN_REFRESH_MAX_RETRIES,
      hasToken: !!idToken,
      tokenValid,
    });

    try {
      const response = await axiosInstance.put(
        `${VITE_ENV.authUrl}/auth/token`,
        {
          client_id: VITE_ENV.cognito.clientId,
        },
        config
      );

      const { id_token: newIdToken } = response.data;
      setSession(newIdToken);

      loggerService.log(LoggerLevel.DEBUG, '[TOKEN_REFRESH] Token refresh successful', {
        attempt,
      });
      return newIdToken;
    } catch (error) {
      const errorDetails = axios.isAxiosError(error)
        ? {
            status: error.response?.status,
            statusText: error.response?.statusText,
            errorData: error.response?.data,
            url: error.config?.url,
          }
        : {
            errorType: error instanceof Error ? error.constructor.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
          };

      if (isTransientRefreshError(error) && attempt < TOKEN_REFRESH_MAX_RETRIES) {
        const delayMs = TOKEN_REFRESH_INITIAL_DELAY_MS * Math.pow(2, attempt - 1);
        loggerService.log(
          LoggerLevel.DEBUG,
          `[TOKEN_REFRESH] Transient error on attempt ${attempt}/${TOKEN_REFRESH_MAX_RETRIES}, retrying in ${delayMs}ms`,
          errorDetails
        );
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }

      // Definitive auth failure or all retries exhausted
      loggerService.log(
        LoggerLevel.DEBUG,
        '[LOGOUT_TRIGGER] Token refresh failed - triggering logout',
        {
          ...errorDetails,
          attempt,
          maxRetries: TOKEN_REFRESH_MAX_RETRIES,
          wasTransient: isTransientRefreshError(error),
          hasToken: !!idToken,
          tokenValid,
        }
      );

      await externalLogout({
        skipRefreshTokenRevocation: true,
        skipLogoutWhenOffline: true,
      });

      return '';
    }
  }

  return '';
};

/**
 * Remove the refresh token from the auth service
 * to prevent further refreshes of the ID token.
 */
// eslint-disable-next-line import/no-unused-modules
export const revokeRefreshToken = async (): Promise<void> => {
  // Get the current token before it gets cleared
  const idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';

  const config: AxiosRequestConfig = {
    data: {
      client_id: VITE_ENV.cognito.clientId,
    },
  };

  // Include authorization if we have a token
  if (idToken) {
    config.headers = {
      Authorization: getAuthorizationHeader(idToken),
    };
  }

  return axiosInstance.delete(`${VITE_ENV.authUrl}/auth/token`, config);
};

export const getLoginUrl = (): string => {
  const redirectUrl = encodeURIComponent(`${VITE_ENV.authUrl}/auth/token`);
  const responseType = 'code';
  const scope = 'email+openid';

  interface CognitoQueryParamState {
    client_id: string;
    dev_redirect_url?: string;
  }
  const cognitoQueryParamState: CognitoQueryParamState = {
    client_id: VITE_ENV.cognito.clientId,
  };

  // FIXME: can we get rid of this?? Please?
  // if (VITE_ENV.isLocalhost) {
  //   cognitoQueryParamState.dev_redirect_url = AUTH_API.devRedirectUrl;
  // }
  //
  const encodedState = window.btoa(JSON.stringify(cognitoQueryParamState));

  return (
    `${VITE_ENV.cognito.hostedLoginUrl}/login?` +
    `client_id=${VITE_ENV.cognito.clientId}&` +
    `state=${encodedState}&` +
    `response_type=${responseType}&` +
    `scope=${scope}&` +
    `redirect_uri=${redirectUrl}`
  );
};

/**
 * Navigating to this endpoint will delete the AWS Cognito session.
 * The user will be redirected to the login page.
 */
// eslint-disable-next-line import/no-unused-modules
export const getLogoutUrl = (): string =>
  `${VITE_ENV.cognito.hostedLoginUrl}/logout?client_id=${
    VITE_ENV.cognito.clientId
  }&logout_uri=${encodeURIComponent(`${VITE_ENV.cognito.callbackUrl}/auth/login`)}`;

// eslint-disable-next-line import/no-unused-modules
export const getForgotPasswordUrl = (): string =>
  `${VITE_ENV.cognito.hostedLoginUrl}/forgotPassword?client_id=${
    VITE_ENV.cognito.clientId
  }&response_type=token&scope=email+openid&redirect_uri=${encodeURIComponent(
    `${VITE_ENV.cognito.callbackUrl}/auth/cognito-callback`
  )}`;
